import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLicenseBanner } from '../context/LicenseBannerContext';
import { NAVIGATION, ROLES } from '../utils/constants';
import inchaaLogo from '../assets/images/inchaa-logo-white.svg';
import { useTranslation } from '../hooks/useTranslation';
import {
  HomeIcon,
  CheckCircleIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  StarIcon,
  GiftIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  FolderIcon,
  BuildingStorefrontIcon,
  HeartIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';

const iconMap = {
  HomeIcon,
  CheckCircleIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  Cog6ToothIcon,
  UserIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ChartBarIcon,
  StarIcon,
  GiftIcon,
  BriefcaseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  FolderIcon,
  BuildingStorefrontIcon,
  HeartIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
};

const Sidebar = ({ sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { isRTL, language, t } = useTranslation();
  const { bannerHeight } = useLicenseBanner();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, right: undefined });
  const groupRefs = useRef({});
  const buttonRefs = useRef({});
  const navRef = useRef(null);

  const rawNavigation = useMemo(() => NAVIGATION[user?.role] || [], [user?.role]);

  const translateName = useCallback((name) => {
    // Superadmin menu items from JSON (ar.js / en.js) - works for both en and ar
    if (name === 'Contractors') return t('menu.contractors');
    if (name === 'Engineers') return t('menu.engineers');
    if (name === 'Consultants') return t('menu.consultants');
    if (name === 'Customers') return t('menu.customers');
    if (language !== 'ar') return name;
    switch (name) {
      case 'Dashboard':
        return 'لوحة القيادة';
      case 'Requested Quotes':
        return 'العروض المطلوبة';
      case 'Submitted Quotes':
        return 'العروض المقدمة';
      case 'Post Ads':
        return 'نشر الإعلانات';
      case 'Work Summary':
        return 'ملخص العمل';
      case 'My Account':
        return 'حسابي';
      case 'Requests':
        return 'الطلبات';
      case 'Quotations':
        return 'عروض الأسعار';
      case 'Suppliers':
        return 'الموردون';
      case 'Reports':
        return 'التقارير';
      case 'Wishlist':
        return 'قائمة الأمنيات';
      case 'Orders Placed':
        return 'الطلبات المقدمة';
      case 'Payments':
        return 'المدفوعات';
      case 'Support':
        return 'الدعم';
      case 'User Approvals':
        return 'موافقات المستخدمين';
      case 'Quotes & Bookings':
        return 'العروض والحجوزات';
      case 'Ads':
        return 'الإعلانات';
      case 'Settings':
        return 'الإعدادات';
      case 'Content Management':
        return 'إدارة المحتوى';
      case 'Offers':
        return 'العروض';
      case 'Materials':
        return 'المواد';
      case 'Featured Providers':
        return 'المزودون المميزون';
      case 'License Management':
        return 'إدارة التراخيص';
      default:
        return name;
    }
  }, [language, t]);

  const navigation = useMemo(() => {
    return rawNavigation.map((item) => {
      if (item.group && item.children) {
        return {
          ...item,
          name: translateName(item.name),
          children: item.children.map((child) => ({
            ...child,
            name: translateName(child.name),
          })),
        };
      }

      return {
        ...item,
        name: translateName(item.name),
      };
    });
  }, [rawNavigation, translateName]);

  // Auto-expand groups if any child is active
  useEffect(() => {
    const newExpandedGroups = {};
    rawNavigation.forEach((item, index) => {
      if (item.group && item.children) {
        const hasActiveChild = item.children.some((child) => {
          if (child.path === '/service-provider/invitations') {
            return location.pathname === child.path || (location.pathname.includes('/service-provider/quote-details') && !location.state?.fromQuotesPage);
          }
          if (child.path === '/service-provider/quotes') {
            return location.pathname === child.path || (location.pathname.includes('/service-provider/quote-details') && location.state?.fromQuotesPage);
          }
          return location.pathname === child.path;
        });
        if (hasActiveChild) {
          newExpandedGroups[index] = true;
        }
      }
    });
    setExpandedGroups((prev) => {
      const next = { ...prev, ...newExpandedGroups };
      // Avoid unnecessary state updates (prevents update loops)
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      for (const k of nextKeys) {
        if (prev[k] !== next[k]) return next;
      }
      return prev;
    });
    
    // Scroll to active submenu after auto-expansion
    const timeoutId = setTimeout(() => {
      const activeIndex = Object.keys(newExpandedGroups).find(index => newExpandedGroups[index]);
      if (activeIndex !== undefined) {
        const groupElement = groupRefs.current[`group-${activeIndex}`];
        if (groupElement) {
          groupElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.state?.fromQuotesPage, rawNavigation]);

  const toggleGroup = (index) => {
    setExpandedGroups((prev) => {
      const isExpanding = !prev[index];
      const newState = {
        ...prev,
        [index]: isExpanding,
      };
      
      // Scroll to submenu when expanding
      if (isExpanding) {
        setTimeout(() => {
          const groupElement = groupRefs.current[`group-${index}`];
          if (groupElement) {
            groupElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        }, 100);
      }
      
      return newState;
    });
  };

  const isActive = (path) => {
    // For service provider invitations, also consider quote-details pages as active
    if (path === '/service-provider/invitations') {
      return location.pathname === path || (location.pathname.includes('/service-provider/quote-details') && !location.state?.fromQuotesPage);
    }
    if (path === '/service-provider/quotes') {
      return location.pathname === path || (location.pathname.includes('/service-provider/quote-details') && location.state?.fromQuotesPage);
    }
    if (path === '/superadmin/customers') {
      return location.pathname === path || location.pathname.startsWith('/superadmin/customer-detail/');
    }
    if (path === '/superadmin/ads') {
      return location.pathname === path || location.pathname.startsWith('/superadmin/ads/');
    }
    if (path === '/service-provider/ads') {
      return location.pathname === path || location.pathname.startsWith('/service-provider/ads/');
    }
    return location.pathname === path;
  };

  const hasActiveChild = (children) => {
    if (!children || !Array.isArray(children)) return false;
    return children.some((child) => {
      if (child.path === '/service-provider/invitations') {
        return location.pathname === child.path || (location.pathname.includes('/service-provider/quote-details') && !location.state?.fromQuotesPage);
      }
      if (child.path === '/service-provider/quotes') {
        return location.pathname === child.path || (location.pathname.includes('/service-provider/quote-details') && location.state?.fromQuotesPage);
      }
      return location.pathname === child.path;
    });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLES.SUPERADMIN:
        return 'Super Admin';
      case ROLES.CONTRACTOR:
        return 'Contractor';
      case ROLES.CONSULTANT:
        return 'Consultant';
      case ROLES.ENGINEER:
        return 'Engineer';
      default:
        return role;
    }
  };

  return (
    <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`} style={{ top: `${bannerHeight}px` }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className={`fixed flex w-64 flex-col bg-white ${isRTL ? 'right-0' : 'left-0'}`} style={{ top: `${bannerHeight}px`, bottom: 0 }}>
          <div className="flex h-16 items-center justify-between px-4 flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900">Inchaa Admin</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav ref={navRef} className="flex-1 space-y-1 px-2 py-4 overflow-y-auto min-h-0 scrollbar-hide">
            {navigation.map((item, index) => {
              if (item.group && item.children) {
                const isExpanded = expandedGroups[index];
                const GroupIcon = iconMap[item.icon] || FolderIcon;
                return (
                  <div key={item.name} ref={(el) => { groupRefs.current[`group-${index}`] = el; }} className="mb-1">
                    <button
                      onClick={() => toggleGroup(index)}
                      className={`w-full flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900`}
                    >
                      <GroupIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                      <span className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>{item.name}</span>
                      {isExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : isRTL ? (
                        <ChevronLeftIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} mt-1 space-y-1`}>
                        {item.children.map((child) => {
                          const ChildIcon = iconMap[child.icon];
                          return (
                            <Link
                              key={child.name}
                              to={child.path}
                              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                                isActive(child.path)
                                  ? 'bg-blue-100 text-blue-900'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <ChildIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                              {child.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const Icon = iconMap[item.icon];
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.path)
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar with collapse support */}
      <div className={`hidden lg:fixed lg:flex lg:flex-col bg-gradient-to-b from-[#3D4EB2] to-[#08086F] text-white z-40 transition-all duration-300 ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'} ${isRTL ? 'lg:right-0' : 'lg:left-0'}`} style={{ willChange: 'transform, width', top: `${bannerHeight}px`, bottom: 0, overflow: 'visible' }}>
        <div className="flex flex-col flex-grow min-h-0" style={{ overflow: 'visible' }}>
          <div className={`flex h-20 items-center px-6 flex-shrink-0 ${sidebarCollapsed ? 'justify-center' : ''}`}>
                          <img 
                             src={inchaaLogo}
               alt="Inchaa Logo" 
               className={`h-8 transition-all duration-200 ${sidebarCollapsed ? 'block' : 'block'}`}
              />
          </div>
          <nav ref={navRef} className="flex-1 space-y-1 px-2 py-4 overflow-y-auto overflow-x-visible min-h-0 scrollbar-hide">
            {navigation.map((item, index) => {
              if (item.group && item.children) {
                const isExpanded = expandedGroups[index];
                const GroupIcon = iconMap[item.icon] || FolderIcon;
                if (sidebarCollapsed) {
                  // When collapsed, show only the group icon with submenu popover on hover
                  const isHovered = hoveredGroup === index;
                  const hasActive = hasActiveChild(item.children);
                  return (
                    <div 
                      key={item.name} 
                      ref={(el) => { groupRefs.current[`group-${index}`] = el; }} 
                      className="relative" 
                      style={{ overflow: 'visible' }}
                      onMouseEnter={(e) => {
                        const button = buttonRefs.current[`button-${index}`];
                        if (button) {
                          const rect = button.getBoundingClientRect();
                          if (isRTL) {
                            // In RTL, sidebar is on right, popover should appear to the left
                            setPopoverPosition({
                              top: rect.top,
                              right: window.innerWidth - rect.left + 8,
                              left: 'auto'
                            });
                          } else {
                            setPopoverPosition({
                              top: rect.top,
                              left: rect.right + 8,
                              right: 'auto'
                            });
                          }
                        }
                        setHoveredGroup(index);
                      }}
                      onMouseLeave={() => setHoveredGroup(null)}
                    >
                      <button
                        ref={(el) => { buttonRefs.current[`button-${index}`] = el; }}
                        className={`w-full flex items-center justify-center px-3 py-3 text-base font-medium rounded-xl transition-all duration-150 mb-1 ${
                          hasActive
                            ? 'bg-white text-[#3D4EB2] shadow'
                            : 'text-white hover:bg-[#08086F]/30 hover:text-white'
                        }`}
                      >
                        <GroupIcon className="h-6 w-6" />
                      </button>
                      {/* Submenu popover for collapsed state */}
                      {isHovered && (
                        <div 
                          className="fixed"
                          style={{ 
                            zIndex: 10000, 
                            top: `${popoverPosition.top}px`,
                            left: popoverPosition.left !== undefined ? `${popoverPosition.left}px` : 'auto',
                            right: popoverPosition.right !== undefined ? `${popoverPosition.right}px` : 'auto'
                          }}
                          onMouseEnter={() => setHoveredGroup(index)}
                          onMouseLeave={() => setHoveredGroup(null)}
                        >
                          <div className="bg-gradient-to-b from-[#3D4EB2] to-[#08086F] text-white text-sm rounded-lg shadow-xl py-2 min-w-[180px] relative">
                            {/* Arrow pointer */}
                            <div 
                              className={`absolute top-4 w-0 h-0 border-t-8 border-b-8 border-t-transparent border-b-transparent ${
                                isRTL 
                                  ? 'right-[-8px] border-r-8 border-r-[#08086F]' 
                                  : 'left-[-8px] border-l-8 border-l-[#08086F]'
                              }`}
                            ></div>
                            {/* Submenu items */}
                            <div className="py-1">
                              {item.children.map((child) => {
                                const ChildIcon = iconMap[child.icon];
                                return (
                                  <Link
                                    key={child.name}
                                    to={child.path}
                                    className={`flex items-center px-4 py-2 hover:bg-[#08086F]/30 transition-colors ${
                                      isActive(child.path) ? 'bg-white/20 text-white' : 'text-white'
                                    }`}
                                  >
                                    {ChildIcon && <ChildIcon className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />}
                                    <span>{child.name}</span>
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                const hasActive = hasActiveChild(item.children);
                return (
                  <div key={item.name} ref={(el) => { groupRefs.current[`group-${index}`] = el; }} className="mb-1">
                    <button
                      onClick={() => toggleGroup(index)}
                      className={`w-full flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-150 mb-1 ${
                        hasActive
                          ? 'bg-white text-[#3D4EB2] shadow'
                          : 'text-white hover:bg-[#08086F]/30 hover:text-white'
                      }`}
                    >
                      <GroupIcon className="h-6 w-6" />
                      <span className={`${isRTL ? 'mr-4' : 'ml-4'} flex-1 ${isRTL ? 'text-right' : 'text-left'} transition-all duration-200`}>{item.name}</span>
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : isRTL ? (
                        <ChevronLeftIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className={`${isRTL ? 'mr-4' : 'ml-4'} mt-1 space-y-1`}>
                        {item.children.map((child) => {
                          const ChildIcon = iconMap[child.icon];
                          return (
                            <Link
                              key={child.name}
                              to={child.path}
                              className={`group flex items-center px-4 py-3 text-base font-medium rounded-xl transition-all duration-150 mb-1 ${
                                isActive(child.path)
                                  ? 'bg-white text-[#3D4EB2] shadow'
                                  : 'text-white hover:bg-[#08086F]/30 hover:text-white'
                              }`}
                            >
                              <ChildIcon className="h-6 w-6" />
                              <span className={`${isRTL ? 'mr-4' : 'ml-4'} transition-all duration-200`}>{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              const Icon = iconMap[item.icon];
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`group flex items-center ${sidebarCollapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'} text-base font-medium rounded-xl transition-all duration-150 mb-1 ${
                    isActive(item.path)
                      ? 'bg-white text-[#3D4EB2] shadow'
                      : 'text-white hover:bg-[#08086F]/30 hover:text-white'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className={`${isRTL ? 'mr-4' : 'ml-4'} transition-all duration-200 ${sidebarCollapsed ? 'hidden' : 'inline'}`}>{item.name}</span>
                </Link>
              );
            })}
          </nav>
          {/* Collapse/Expand button */}
          <div className="flex items-center justify-center py-4 border-t border-[#08086F]/30 flex-shrink-0">
            <button
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#08086F]/30 transition"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 